import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { withTranslation } from 'react-i18next';
import {
  Loader, Label, Icon, Comment, Pagination, Message,
} from 'semantic-ui-react';
import styled from 'styled-components';

import FileChooser from 'components/lib/Choosers/FileChooser';
import { GiphyPicker, GiphyGif } from 'components/lib/Giphy';

import MentionText from 'components/lib/Editors/RichText/MentionText';
import FeedComment from './FeedComment';
import { InputContainer, ActionArea, ActionButton } from './Actions';

const PaginationArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 10px;
`;

const NoComments = styled.div`
  display: flex;
  height: 100px;
  justify-content: center;
  align-items: center;
  span {
    display: block;
    font-size: 1.2em;
    font-weight: 600;
    color: #585858;
  }
`;

const DiscussionDiv = styled.div`
  .comment{
    display:block;
    clear:both;
  }
  .custom-margin{
    margin-top: 10px;
  }

  .pinned-comment{
    background-color: rgb(255, 255, 197) !important;
    padding: 10px 5px  !important;
    margin-bottom: 9px !important;
    border-radius: 5px;
    box-shadow: 1px 1px 5px grey;
  }

  .feed-replies{
    margin: 0;
    padding: 0;
  }

  .ui.toggle.small.checkbox.custom-checkbox{
    margin-right: 20px;
  }

  .map.pin.icon{
    font-size: 20px;
  }

  .comment-img{
    max-width: 100%;
  }

  .comment-media-previews{
    margin:8px 0px;
    .comment-file-preview{
      position:relative;
      width:100%;
      height:150px;
      text-align:center;
      border:1px solid rgb(230,230,230);
      border-radius:4px;
      margin:5px;
      padding:4px;
      display:inline-block;
      h2{
        margin-top:0px;
      }
    }
    .comment-image-preview{
      position:relative;
      width:100%;
      height:150px;
      background-size:cover;
      background-repeat:no-repeat;
      background-position:center center;
      margin: 5px;
      padding:5px;
      display:inline-block;
    }
    .button.js-delete-comment-media{
      position:absolute;
      top:5px;
      left:0px;
      opacity:0.0;
      transition:opacity 0.5s;
    }
    &:hover{
      .button{
        opacity:1.0;
      }
    }
  }
`;

const FeedView = (props) => {
  const {
    t,
    loading,
    commentLoading,
    // Feed comments
    feed = [],
    commentMedia = [],
    // Attachment/file uploading
    addAttachment,
    addGiphy: propAddGiphy,
    deleteAttachment,
    fileChooserForType,
    fileChooserForId,
    commentText,
    updateComment,
    updateReply,
    placeholder,
    postText,
    // Management props to test whether user can alter settings
    canManage,
    canPost,
    // Function called on comment post
    post,
    page,
    nextPageAvailable,
    previousPageAvailable,
    onPageChange,
    total,
    limit,
    // Whether attachments are disabled for this feed
    disableAttachments,
    // Discussion context e.g. ideas, challenge etc
    type,
    message,
    // Meta
    noCommentsText,
    disableMentions,
  } = props;
  const [giphyPickerOpen, setGiphyPickerOpen] = useState(false);
  const [recommendPinning, setRecommendPinning] = useState(false);
  const editorRef = useRef();

  useEffect(() => {
    if (type !== 'idea') {
      const noPinnedComments = feed.findIndex((c) => c.isPinned);
      setRecommendPinning(noPinnedComments === -1);
    }
  }, [feed, type]);

  const isLastChild = useCallback((comment) => {
    const index = feed.slice().reverse().findIndex((c) => !c.parent);
    const count = (feed.length - 1);
    const finalIndex = (index >= 0) ? (count - index) : index;
    const finalComment = feed[finalIndex];
    return finalComment?._id === comment._id;
  }, [feed]);

  const addGiphy = useCallback((gif) => {
    setGiphyPickerOpen(false);
    propAddGiphy(gif);
  }, [propAddGiphy]);

  const addAutoMention = useCallback((parentCommentId, user) => {
    /*
      - The comment box uses rich text to allow various mention compatibility, however the replyText is a plain string
      - The comment box thus manages it's own state, and sends onChanges back here when the HTML changes
      - This means we can't just run updateComment(), because it won't update the reply box
      - Therefore we supply a special insertion string: <?insert!>
      - The Mention box will recognise this special string, and know to insert this text to it's HTML, while removing the special string itself
      - We also need to add the mention as HTML, rather than the standard "(@)[Name](_id:id_here)" format
      - So we supply an a element instead
    */
    const newComment = (`${commentText}(@)[${user.profile.fullName}](_id:${user._id})<?insert!>`);
    // We also need the regular mention - Else the API won't recognise a mention is contained in this text, and be unable
    updateReply(parentCommentId, newComment);
  }, [commentText, updateReply]);

  const totalPages = Math.ceil(total / limit);

  return (
    <DiscussionDiv>
      {message
        ? (
          <Message size='tiny'>
            {message}            
          </Message>
        ) : null
      }
      {(canPost || canManage)
        && <>
          {recommendPinning && canManage
            && <Message size='tiny' info style={{
              display: 'inline-block', width: '100%', marginBottom: 10, marginTop: 10,
            }} >
              <Message.Header>Why not pin a post?</Message.Header>
              <p>Pinning a post can be useful to explain the purpose of the discussion and helps to drive user engagement. To pin a post, find an existing entry in the feed below and tap "Pin comment".</p>
            </Message>
          }
          <InputContainer>
            <MentionText
              placeholder={placeholder}
              value={commentText}
              onChange={(value) => updateComment(value)}
              className="main-comment-box"
              editorRef={editorRef}
            />
            <ActionArea>
              <div>
                {!disableMentions
                  ? (
                    <ActionButton
                      title={'Mention user'}
                      position='top left'
                      iconName='user'
                      onClick={(e) => {
                        e.preventDefault();
                        const newComment = `${commentText}@`;
                        updateComment(newComment);
                        editorRef.current.focus();
                      }}
                    />
                  ) : null
                }
                {!disableAttachments
                  && (
                    <>
                      <FileChooser forType={fileChooserForType} forId={fileChooserForId}
                        trigger={(
                          <ActionButton
                            title={t('feeds.attachments.attach')}
                            iconName='paperclip'
                          />
                        )}
                        onComplete={addAttachment}
                      />
                      <GiphyPicker onChoose={addGiphy}
                        trigger={(
                          <ActionButton
                            title={t('feeds.attachments.gif')}
                            iconName='file image outline'
                            onClick={() => setGiphyPickerOpen(true)}
                          />
                        )}
                        open={giphyPickerOpen}
                        onClose={() => setGiphyPickerOpen(false)}
                      />
                    </>
                  )
                }
              </div>
              <ActionButton
                iconName={commentLoading ? 'spinner' : 'send'}
                onClick={() => post()}
                disabled={!(commentText || commentMedia.length)}
                content={postText || t('feeds.post')}
              />
            </ActionArea>
            <div style={{ display: 'flex' }}>
              {commentMedia.map((file, index) => {
                if (file.type === 'gif') return <GiphyGif onClickGif={() => deleteAttachment(index)} canDelete={true} key={file.value} gif={file.data} size='fixed_small' />;
                return (
                  <Label color='blue' key={file.value}>
                    {file.name} <Icon name='close' onClick={() => deleteAttachment(index)} />
                  </Label>
                );
              })}
            </div>

          </InputContainer>
        </>
      }

      {loading && <Loader active inline='centered' />}
      {!loading && !feed.length
        && <NoComments>
          <span>{noCommentsText || t('challenge.discussion.noneTitle')}</span>
        </NoComments>
      }

      {feed.length > 0
        && <Comment.Group style={{ margin: '1em 0', maxWidth: '100%' }}>
          {feed.filter((i) => i.owner).map((item) => {
            if (!item.parent) return <FeedComment key={item._id} comment={item} isLastChild={isLastChild(item)} addAutoMention={addAutoMention} {...props} />;
            return null;
          })}
        </Comment.Group>
      }

      {(page && total && (nextPageAvailable || previousPageAvailable))
        ? (
          <PaginationArea>
            <Pagination
              onPageChange={(e, { activePage }) => onPageChange(activePage)}
              activePage={page}
              totalPages={totalPages}
              boundaryRange={1} siblingRange={1}
              ellipsisItem={null} firstItem={null} lastItem={null}
              color='blue'
            />
          </PaginationArea>
        ) : null
      }
    </DiscussionDiv>
  );
};

export default withTranslation()(FeedView);
